(function () {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function createCircleSvg() {
    return `
      <svg class="hh-shape hh-shape--circle" preserveAspectRatio="none" viewBox="0 0 239 112" fill="none" aria-hidden="true">
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-width="2.5"
          vector-effect="non-scaling-stroke"
          d="M209.793 7.563c-3.761-.066-7.336-.809-11.059-1.198-10.522-1.1-21.334-1.187-31.894-1.589-17.861-.68-35.657-.632-53.487.755-17.875 1.39-36.15 3.045-53.724 6.72-17.695 3.699-32.947 11.442-45.652 24.193C8.604 41.836 3.724 46.924 1.712 54.49c-1.766 6.646-.057 13.37 3.04 19.376 7.31 14.176 24.289 21.161 38.576 25.938 31.322 10.471 65.275 12.613 98.066 10.417 18.208-1.22 36.408-3.315 54.221-7.344 9.566-2.164 19.614-4.969 28.225-9.844 9.515-5.388 15.133-14.411 14.02-25.392-1.145-11.3-8.044-20.744-15.828-28.594-15.36-15.49-37.406-23.105-58.153-28.569C140.302 4.271 116.23 1 91.863 1"
        />
      </svg>
    `;
  }

  function createUnderlineSvg() {
    return `
      <svg class="hh-shape hh-shape--underline" preserveAspectRatio="none" viewBox="0 0 215 14" fill="none" aria-hidden="true">
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-width="2.5"
          vector-effect="non-scaling-stroke"
          d="M1 1c20.54 2.356 41.237 2.634 61.957 2.703 25.31.085 50.625.028 75.928-.467 19.199-.376 38.386-1.092 57.584-1.503 5.836-.125 11.686-.278 17.526-.278.17 0-4.327.618-4.547.644-29.274 3.52-58.822 5.58-88.31 7.542-26.569 1.768-53.22 2.055-79.861 2.552-2.122.039-4.269.06-6.388.176-2.502.137 4.997.4 7.505.443 46.458.782 92.838-1.125 139.285-1.125"
        />
      </svg>
    `;
  }

  class SplitLines extends HTMLElement {
    connectedCallback() {
      if (this._isSplit) return;
      if (!this._sourceText) {
        this._sourceText = this.textContent.trim();
      }
      requestAnimationFrame(() => this.split());
    }

    split() {
      if (this._isSplit || this._splitting) return;

      const source = (this._sourceText || '').trim();
      if (!source) return;

      this._splitting = true;

      const line = document.createElement('span');
      line.className = 'hh-line';
      line.textContent = source;

      this.replaceChildren(line);
      this._isSplit = true;
      this.classList.add('is-split');
      this._splitting = false;
    }

    get lines() {
      if (!this._isSplit) this.split();
      return Array.from(this.querySelectorAll('.hh-line'));
    }
  }

  class HighlightedHeading extends HTMLElement {
    connectedCallback() {
      if (this.effect === 'none' || this._initialized) return;
      this._initialized = true;

      const instant =
        prefersReducedMotion.matches || (typeof Shopify !== 'undefined' && Shopify.designMode);

      requestAnimationFrame(() => {
        const splitLines = this.querySelector('split-lines');
        if (splitLines && !splitLines._isSplit) splitLines.split();

        if (instant) {
          this._draw({ instant: true });
          return;
        }

        this._observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting) || this._hasRevealed) return;
            this._draw({ instant: false });
            this._observer.disconnect();
            this._observer = null;
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
        );

        this._observer.observe(this);
      });
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._initialized = false;
      this._hasRevealed = false;
    }

    get effect() {
      return this.getAttribute('effect') || 'none';
    }

    _draw({ instant = false } = {}) {
      if (this._hasRevealed || this.effect === 'none') return;

      const lines = this.querySelectorAll('.hh-line');
      if (!lines.length) return;

      const isCircle = this.effect.includes('circle');
      const svgMarkup = isCircle ? createCircleSvg() : createUnderlineSvg();

      lines.forEach((line, index) => {
        line.querySelector('svg')?.remove();
        line.insertAdjacentHTML('beforeend', svgMarkup);

        if (instant) return;

        const path = line.querySelector('path');
        if (!path) return;

        const svg = path.closest('svg');
        const bounds = svg.getBoundingClientRect();
        const bbox = svg.getBBox();
        const scaleFactor = Math.max(bounds.width, bounds.height) / Math.max(bbox.width, bbox.height, 1);
        const pathLength = path.getTotalLength() * scaleFactor;

        path.setAttribute('stroke-dasharray', String(pathLength));
        path.setAttribute('stroke-dashoffset', String(pathLength));

        path.animate(
          { strokeDashoffset: [pathLength, 0] },
          {
            fill: 'forwards',
            easing: 'cubic-bezier(0.83, 0, 0.17, 1)',
            duration: 1000,
            delay: 500 * index,
          }
        );
      });

      this._hasRevealed = true;
      this.classList.add('is-revealed');
    }
  }

  if (!customElements.get('split-lines')) {
    customElements.define('split-lines', SplitLines);
  }

  if (!customElements.get('highlighted-heading')) {
    customElements.define('highlighted-heading', HighlightedHeading);
  }
})();
